import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { BarChart3 } from 'lucide-react';
import { TopCategory } from '@/hooks/useDashboardMetrics';

interface TopCategoriesProps {
  categories: TopCategory[] | undefined;
  isLoading: boolean;
}

const TopCategories = ({ categories, isLoading }: TopCategoriesProps) => {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((j) => (
              <div key={j} className="flex justify-between items-center animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const getPositionIcon = (index: number) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `${index + 1}º`;
  };

  const getPositionColor = (index: number) => {
    if (index === 0) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    if (index === 1) return 'text-gray-600 bg-gray-50 border-gray-200';
    if (index === 2) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-blue-600 bg-blue-50 border-blue-200';
  };

  return (
    <Card className="hover:shadow-lg transition-all duration-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-purple-600">
          <BarChart3 className="h-5 w-5" />
          Top 5 Categorias
        </CardTitle>
      </CardHeader>
      <CardContent>
        {categories && categories.length > 0 ? (
          <div className="space-y-4">
            {categories.map((category, index) => (
              <div 
                key={category.name || `category-${index}`} 
                className={`p-3 rounded-lg border transition-all duration-200 hover:shadow-md ${getPositionColor(index)}`}
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold">
                      {getPositionIcon(index)}
                    </span>
                    <span className="text-sm font-medium truncate">
                      {category.name}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{category.count}</div>
                    <div className="text-xs text-gray-500">{category.percentage}%</div>
                  </div>
                </div>
                <Progress 
                  value={category.percentage} 
                  className="h-2"
                />
              </div>
            ))}

            {/* Resumo */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  Total de Tickets
                </span>
                <span className="font-bold">
                  {categories.reduce((sum, cat) => sum + cat.count, 0)}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-gray-600">
                  Top 5 representam
                </span>
                <span className="text-xs font-semibold text-purple-600">
                  {categories.reduce((sum, cat) => sum + cat.percentage, 0)}% do total
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <BarChart3 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Nenhuma categoria encontrada</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TopCategories;
